"use client"

import { Button } from "@/components/ui/button"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { AlertCircle } from "lucide-react"
import { ProcessedEntry } from "@/types/import"
import { ImportOptions } from "./ImportOptions"
import { BackButton } from "./BackButton"
import useMirrorAcknowledge from "./hooks/useMirrorAcknowledge"

import { ImportOptions as ImportOptionsType } from "../../types"

interface MirrorImportAcknowledgeProps {
  entries: ProcessedEntry[]
  importOptions: ImportOptionsType
  setImportOptions: React.Dispatch<React.SetStateAction<ImportOptionsType>>
  onBack: () => void
  onContinue: (notes: any[]) => void
}

/**
 * Component for acknowledging and confirming Mirror post imports.
 * Acts as an intermediary step in the import flow.
 * Uses a custom hook for state management and logic.
 */
export function MirrorImportAcknowledge(props: MirrorImportAcknowledgeProps) {
  const { entries, importOptions, setImportOptions, onBack, onContinue } = props

  // Custom hook for acknowledge step logic
  const { isLoading, error, handleContinue } = useMirrorAcknowledge(
    entries,
    onContinue
  )

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-base font-semibold">
          Ready to import {entries.length} posts from Mirror
        </h3>
        <p className="text-sm text-muted-foreground">
          We've found {entries.length} posts from your Mirror account. They're
          ready to import into Paragraph.
        </p>
      </div>

      {entries.length > 0 && (
        <div className="space-y-1">
          <p className="text-sm font-medium">Posts to import:</p>
          <ul className="pl-6 text-sm list-disc space-y-1">
            {entries.slice(0, 3).map((entry: ProcessedEntry, i: number) => (
              <li key={i} className="text-muted-foreground">
                {entry.title}
              </li>
            ))}
            {entries.length > 3 && (
              <li className="text-muted-foreground">
                ...and {entries.length - 3} more
              </li>
            )}
          </ul>
        </div>
      )}

      <ImportOptions options={importOptions} setOptions={setImportOptions} />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center gap-3 mt-6">
        <BackButton onClick={onBack} />
        <Button disabled={isLoading} onClick={handleContinue}>
          {isLoading ? "Processing..." : "Acknowledge & Import"}
        </Button>
      </div>
    </div>
  )
}
